import { useCallback, useEffect, useMemo, useState } from 'react'
import type { AIService, ApiLog } from '@/lib/supabase-client'

interface PaginationState {
  page: number
  limit: number
  total: number
}

interface FiltersState {
  serviceId: string
  statusCode: string
  dateFrom: string
  dateTo: string
}

// API 调用辅助函数
const apiCall = async (endpoint: string, options: RequestInit = {}) => {
  const token = localStorage.getItem('admin_token')
  if (!token) {
    throw new Error('未登录')
  }

  const response = await fetch(endpoint, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const error = await response.json()
    throw new Error(error.error || '请求失败')
  }

  return response.json()
}

export default function LogsPage() {
  const [logs, setLogs] = useState<ApiLog[]>([])
  const [services, setServices] = useState<AIService[]>([])
  const [loading, setLoading] = useState(true)
  const [pagination, setPagination] = useState<PaginationState>({
    page: 1,
    limit: 25,
    total: 0,
  })
  const [filters, setFilters] = useState<FiltersState>({
    serviceId: '',
    statusCode: '',
    dateFrom: '',
    dateTo: '',
  })

  const totalPages = useMemo(() => {
    if (pagination.total === 0) return 1
    return Math.ceil(pagination.total / pagination.limit)
  }, [pagination.total, pagination.limit])

  const fetchServices = useCallback(async () => {
    try {
      const result = await apiCall('/api/admin/services')
      const activeServices = (result.data ?? []).filter(
        (service: AIService) => service.status === 'active'
      )
      setServices(activeServices)
    } catch (error) {
      console.error('Failed to fetch services', error)
      alert(error instanceof Error ? error.message : '获取服务列表失败')
    }
  }, [])

  const fetchLogs = useCallback(async () => {
    setLoading(true)

    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      })

      if (filters.serviceId) {
        params.append('service_id', filters.serviceId)
      }
      if (filters.statusCode) {
        params.append('status_code', filters.statusCode)
      }
      if (filters.dateFrom) {
        params.append('date_from', filters.dateFrom)
      }
      if (filters.dateTo) {
        params.append('date_to', filters.dateTo)
      }

      const result = await apiCall(`/api/admin/logs?${params.toString()}`)
      setLogs(result.data ?? [])
      setPagination((previous) => ({
        ...previous,
        total: result.pagination?.total ?? previous.total,
      }))
    } catch (error) {
      console.error('Failed to fetch logs', error)
      alert(error instanceof Error ? error.message : '获取日志失败')
    } finally {
      setLoading(false)
    }
  }, [filters, pagination.limit, pagination.page])

  useEffect(() => {
    void fetchServices()
  }, [fetchServices])

  useEffect(() => {
    void fetchLogs()
  }, [fetchLogs])

  const handleFilterChange = (key: keyof FiltersState, value: string) => {
    setFilters((previous) => ({
      ...previous,
      [key]: value,
    }))
    setPagination((previous) => ({ ...previous, page: 1 }))
  }

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return
    setPagination((previous) => ({ ...previous, page }))
  }

  const exportLogs = async () => {
    try {
      // 获取所有日志用于导出（不分页）
      const params = new URLSearchParams({
        page: '1',
        limit: '10000', // 获取大量数据用于导出
      })

      if (filters.serviceId) {
        params.append('service_id', filters.serviceId)
      }
      if (filters.statusCode) {
        params.append('status_code', filters.statusCode)
      }
      if (filters.dateFrom) {
        params.append('date_from', filters.dateFrom)
      }
      if (filters.dateTo) {
        params.append('date_to', filters.dateTo)
      }

      const result = await apiCall(`/api/admin/logs?${params.toString()}`)
      const data = result.data ?? []

      const csvHeader =
        'Timestamp,Method,Path,Status,Response Time (ms),Points,Service,IP,User Agent\n'
      const csvRows = data.map((row: any) =>
        [
          row.created_at,
          row.request_method,
          row.request_path?.replace(/,/g, ';'),
          row.response_status_code,
          row.response_time_ms,
          row.points_charged,
          row.service_name ?? '',
          row.ip_address ?? '',
          (row.user_agent ?? '').replace(/,/g, ';'),
        ].join(',')
      )

      const blob = new Blob([csvHeader, ...csvRows.join('\n')], {
        type: 'text/csv',
      })
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = `api-logs-${new Date().toISOString()}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Failed to export logs', error)
      alert(error instanceof Error ? error.message : '导出日志失败')
    }
  }

  return (
    <section className="h-full w-full p-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-gray-900">API 调用日志</h2>
          <p className="mt-1 text-sm text-gray-600">
            实时检查代理调用、延迟和积分消耗情况
          </p>
        </div>
        <button
          onClick={exportLogs}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          导出 CSV
        </button>
      </div>

      <div className="mb-4 grid gap-4 rounded-lg bg-white p-4 shadow md:grid-cols-4">
        <label className="text-xs font-medium uppercase text-gray-500">
          服务
          <select
            value={filters.serviceId}
            onChange={(event) => handleFilterChange('serviceId', event.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          >
            <option value="">全部</option>
            {services.map((service) => (
              <option key={service.id} value={service.id}>
                {service.name}
              </option>
            ))}
          </select>
        </label>

        <label className="text-xs font-medium uppercase text-gray-500">
          状态码
          <input
            type="number"
            value={filters.statusCode}
            onChange={(event) => handleFilterChange('statusCode', event.target.value)}
            placeholder="例如: 200"
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>

        <label className="text-xs font-medium uppercase text-gray-500">
          开始日期
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(event) => handleFilterChange('dateFrom', event.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>

        <label className="text-xs font-medium uppercase text-gray-500">
          结束日期
          <input
            type="date"
            value={filters.dateTo}
            onChange={(event) => handleFilterChange('dateTo', event.target.value)}
            className="mt-1 w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
          />
        </label>
      </div>

      <div className="overflow-hidden rounded-lg bg-white shadow">
        {loading ? (
          <div className="flex min-h-[200px] items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  时间戳
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  方法
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  路径
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  状态码
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  响应时间
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  积分
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  服务
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                  IP地址
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white text-sm">
              {logs.map((log) => (
                <tr key={log.id}>
                  <td className="px-4 py-3 text-gray-700">
                    {new Date(log.created_at).toLocaleString()}
                  </td>
                  <td className="px-4 py-3 font-semibold text-blue-600">
                    {log.request_method}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{log.request_path}</td>
                  <td className="px-4 py-3 text-gray-700">{log.response_status_code}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {log.response_time_ms ? `${log.response_time_ms}ms` : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-700">{log.points_charged}</td>
                  <td className="px-4 py-3 text-gray-700">
                    {(log as any).service_name ?? '未知'}
                  </td>
                  <td className="px-4 py-3 text-gray-500">{log.ip_address ?? '—'}</td>
                </tr>
              ))}
              {logs.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-10 text-center text-gray-500">
                    未找到日志记录
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      {totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between text-sm">
          <span className="text-gray-600">
            第 {pagination.page} 页，共 {totalPages} 页
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page === 1}
              className="rounded-md border border-gray-300 px-3 py-1 text-gray-700 disabled:opacity-50"
            >
              上一页
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page === totalPages}
              className="rounded-md border border-gray-300 px-3 py-1 text-gray-700 disabled:opacity-50"
            >
              下一页
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
